import { createContext, useContext, useEffect, useState } from "react";
import { Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Home, UtensilsCrossed, Search, ShieldAlert, Instagram, Phone, MapPin, ChevronDown } from "lucide-react";
import MenuHome from "@/menu/MenuHome";
import CategoryPage from "@/menu/CategoryPage";
import DishPage from "@/menu/DishPage";
import SearchPage from "@/menu/SearchPage";
import AllergensPage from "@/menu/AllergensPage";
import AdminLogin from "@/menu/admin/AdminLogin";
import AdminPanel from "@/menu/admin/AdminPanel";
import { STR, LANGS, sget, Flag } from "@/menu/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/menu`;

export const MenuCtx = createContext(null);
export const useMenu = () => useContext(MenuCtx);

const LANG_NAMES = { it: "Italiano", en: "English", fr: "Français", es: "Español", de: "Deutsch" };

const LangDropdown = ({ lang, setLang }) => {
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === lang) || LANGS[0];
  return (
    <div className="relative" data-testid="menu-lang-selector">
      <button
        data-testid="menu-lang-toggle"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 rounded-full border border-[#C19D60]/30 px-3.5 py-1.5 text-[11px] font-medium tracking-wider text-[#E5CAA4] transition-colors duration-300 hover:border-[#C19D60]/60"
      >
        <Flag country={current.country} /> {current.label}
        <ChevronDown size={12} className={`transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div data-testid="menu-lang-dropdown" className="absolute right-0 z-50 mt-2 w-44 overflow-hidden rounded-xl border border-[#C19D60]/25 bg-[#101012]/95 shadow-[0_20px_50px_-10px_rgba(0,0,0,0.8)] backdrop-blur-xl">
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`menu-lang-${l.code}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
                className={`flex w-full items-center gap-3 px-4 py-3 text-left text-xs transition-colors duration-200 ${
                  lang === l.code ? "bg-[#C19D60]/15 text-[#E5CAA4]" : "text-[#c9c9c9] hover:bg-white/5"
                }`}
              >
                <Flag country={l.country} className="h-4 w-6 rounded-[2px] object-cover" />
                <span className="font-medium">{LANG_NAMES[l.code]}</span>
                <span className="ml-auto text-[9px] uppercase tracking-widest text-[#666]">{l.label}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const MenuApp = () => {
  const [data, setData] = useState(null);
  const [lang, setLangState] = useState(() => localStorage.getItem("menu-lang") || "it");
  const [translating, setTranslating] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const load = () => axios.get(`${API}/public`).then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem("menu-lang", lang); }, [lang]);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [pathname]);

  const setLang = async (l) => {
    setLangState(l);
    if (["fr", "es", "de"].includes(l) && data && data.dishes.some((d) => !d.name[l])) {
      setTranslating(true);
      try {
        await axios.post(`${API}/translate/${l}`);
        await load();
      } catch {}
      setTranslating(false);
    }
  };

  const t = (key) => (STR[lang] || STR.it)[key] || key;
  const s = data?.settings;

  return (
    <MenuCtx.Provider value={{ data, reload: load, lang, setLang, t }}>
      <div data-testid="menu-app" className="menu-marble min-h-screen text-[#F8F5F0]" style={{ fontFamily: "'Manrope', sans-serif" }}>
        {translating && (
          <div data-testid="menu-translating" className="fixed inset-0 z-[90] flex items-center justify-center bg-[#0a0a0b]/90 backdrop-blur-md">
            <p className="font-display animate-pulse text-2xl italic text-[#C19D60]">{t("translating")}</p>
          </div>
        )}
        <header data-testid="menu-header" className="fixed inset-x-0 top-0 z-50 border-b border-[#C19D60]/15 bg-[#0a0a0b]/85 backdrop-blur-xl">
          <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3.5">
            <Link to="/menu" data-testid="menu-header-logo" className="flex items-baseline gap-2.5">
              <span className="font-display text-xl font-semibold tracking-wide text-[#F8F5F0]">{s?.name || "L'ARTE DEL GUSTO"}</span>
              <span className="hidden text-[10px] font-light uppercase tracking-[0.25em] text-[#C19D60] sm:inline">{sget(s, "claim", lang)}</span>
            </Link>
            <LangDropdown lang={lang} setLang={setLang} />
          </div>
        </header>

        <div className="pb-24 pt-[57px] md:pb-0">
          {!data ? (
            <div className="flex min-h-[60vh] items-center justify-center">
              <p className="font-display animate-pulse text-2xl italic text-[#C19D60]">…</p>
            </div>
          ) : (
            <Routes>
              <Route index element={<MenuHome />} />
              <Route path="categoria/:slug" element={<CategoryPage />} />
              <Route path="piatto/:id" element={<DishPage />} />
              <Route path="cerca" element={<SearchPage />} />
              <Route path="allergeni" element={<AllergensPage />} />
              <Route path="admin/login" element={<AdminLogin />} />
              <Route path="admin" element={<AdminPanel />} />
            </Routes>
          )}
        </div>

        <footer data-testid="menu-footer" className="border-t border-[#C19D60]/15 bg-[#060607] px-5 pb-24 pt-14 md:pb-14">
          <div className="mx-auto max-w-5xl">
            <div className="flex flex-col items-start justify-between gap-10 md:flex-row">
              <div>
                <p className="font-display text-3xl font-semibold">{s?.name || "L'ARTE DEL GUSTO"}</p>
                <p className="mt-2 text-sm font-light text-[#A3A3A3]">{sget(s, "claim", lang)}</p>
              </div>
              <div className="space-y-2.5 text-sm font-light text-[#A3A3A3]">
                <Link to="/menu/allergeni" data-testid="menu-footer-allergens" className="block transition-colors hover:text-[#E5CAA4]">{t("allergenInfo")}</Link>
                <a href="/privacy" data-testid="menu-footer-privacy" className="block transition-colors hover:text-[#E5CAA4]">{t("privacy")}</a>
                <a href="/cookie-policy" data-testid="menu-footer-cookie" className="block transition-colors hover:text-[#E5CAA4]">Cookie Policy</a>
              </div>
              <div className="space-y-2.5 text-sm font-light text-[#A3A3A3]">
                <a href={`https://instagram.com/${s?.instagram || ""}`} target="_blank" rel="noreferrer" data-testid="menu-footer-instagram" className="flex items-center gap-2.5 transition-colors hover:text-[#E5CAA4]">
                  <Instagram size={15} strokeWidth={1.4} className="text-[#C19D60]" /> @{s?.instagram}
                </a>
                <a href={`tel:${(s?.phone || "").replace(/\s/g, "")}`} data-testid="menu-footer-phone" className="flex items-center gap-2.5 transition-colors hover:text-[#E5CAA4]">
                  <Phone size={15} strokeWidth={1.4} className="text-[#C19D60]" /> {s?.phone}
                </a>
                <p className="flex items-center gap-2.5"><MapPin size={15} strokeWidth={1.4} className="text-[#C19D60]" /> {s?.address}</p>
              </div>
            </div>
            <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/5 pt-6 sm:flex-row">
              <Link to="/menu/admin" data-testid="menu-footer-admin" className="text-[10px] font-light uppercase tracking-[0.2em] text-[#666] transition-colors hover:text-[#E5CAA4]">
                {t("reserved")}
              </Link>
              <a href="/" data-testid="menu-badge" className="text-[10px] font-light uppercase tracking-[0.2em] text-[#C19D60]/80 transition-colors hover:text-[#E5CAA4]">
                {sget(s, "badge", lang)} →
              </a>
            </div>
          </div>
        </footer>

        <nav data-testid="menu-bottom-nav" className="fixed inset-x-0 bottom-0 z-50 grid grid-cols-4 border-t border-[#C19D60]/15 bg-[#0a0a0b]/92 backdrop-blur-xl md:hidden">
          {[
            { to: "/menu", icon: Home, label: t("home"), id: "home" },
            { to: "/menu", icon: UtensilsCrossed, label: t("menu"), id: "menu", hash: true },
            { to: "/menu/cerca", icon: Search, label: t("search"), id: "search" },
            { to: "/menu/allergeni", icon: ShieldAlert, label: t("allergens"), id: "allergens" },
          ].map((item) => (
            <button
              key={item.id}
              data-testid={`menu-nav-${item.id}`}
              onClick={() => {
                if (item.hash) {
                  navigate("/menu");
                  setTimeout(() => document.getElementById("esplora")?.scrollIntoView({ behavior: "smooth" }), 150);
                } else {
                  navigate(item.to);
                  window.scrollTo({ top: 0 });
                }
              }}
              className="flex flex-col items-center gap-1 py-3 text-[10px] font-medium tracking-wide text-[#A3A3A3] transition-colors active:text-[#C19D60]"
            >
              <item.icon size={19} strokeWidth={1.4} />
              {item.label}
            </button>
          ))}
        </nav>
      </div>
    </MenuCtx.Provider>
  );
};

export default MenuApp;
